<script lang="ts">
    
    import { onDestroy } from "svelte";
    import type { RealTimeEvent } from "../../global";
    import type { Message } from "../../Model/Message";
    import { eventsChannelsFactory, messagesFactory, messagesService } from "../../Services";
    import type { EventsChannel } from "../../Services/EventsChannel";

    export let roomId: string;

    const animationsMap = new Map<HTMLElement, number>();
    const messagesVelocity = 0.07;
    
    let maxTransitions = 3;
    let marqueeContainer: HTMLElement;
    let roomEvents: EventsChannel;
    let messages: Message[] = [];
    let enabledMessages: Message[] = [];
    let iterationsCount = 0;
    let currentMessageIndex = 0;

    $: setupRoom(roomId);

    onDestroy(() => {
        //Cancelamos todas las animaciones
        for (const animationId of animationsMap.values()) {
            window.cancelAnimationFrame(animationId);
        }
        animationsMap.clear();
    });

    async function setupRoom(roomId: string): Promise<void> {
        roomEvents?.destroy();
        roomEvents = eventsChannelsFactory.createEventsChannel(`room.${roomId}`);
        roomEvents.on('message_added', onMessageAdded);
        roomEvents.on('message_updated', onMessageUpdated);
        roomEvents.on('message_enabled', onMessageUpdated);
        roomEvents.on('message_disabled', onMessageDisabled);
        roomEvents.on('message_removed', onMessageRemoved);
        messages = await messagesService.getRoomMessages(roomId);
        currentMessageIndex = 0;
        filterEnabledMessages(messages);
    }

    function filterEnabledMessages(messages: Message[]): void {
        const prevEnabledMessagesCount = enabledMessages.length;
        enabledMessages = messages.filter(({ enabled }) => enabled.value);
        if (prevEnabledMessagesCount == 0 && enabledMessages.length > 0) {
            nextMessage();
        }
    }

    function nextMessage(): void {
        if (enabledMessages.length === 0) {
            return;
        }

        if (iterationsCount >= maxTransitions) {
            iterationsCount = 0;
            currentMessageIndex++;
        }

        if (currentMessageIndex >= enabledMessages.length) {
            currentMessageIndex = 0;
        }
        
        iterationsCount++;
        
        const message = enabledMessages[currentMessageIndex];
        const messageElement = document.createElement('div');
        messageElement.classList.add('message');
        messageElement.textContent = message.text.value;
        marqueeContainer.append(messageElement);
        animateMessage(messageElement);
    }

    function onMessageAdded({ data }: RealTimeEvent): void {
        const { roomId, id, enabled, text } = data.message;
        const message = messagesFactory.create(roomId, id, enabled, text);
        messages = [...messages, message];
        filterEnabledMessages(messages);
    }

    function onMessageUpdated(event: RealTimeEvent): void {
        const message = messages.find(({ id }) => id === event.data.message.id);
        if (!message) {
            onMessageAdded(event);
            return;
        }
        message.text.set(event.data.message.text);
        message.enabled.set(event.data.message.enabled);
        filterEnabledMessages(messages);
    }
    
    function onMessageDisabled({ data }: RealTimeEvent): void {
        if (!messages.some(({ id }) => id === data.message.id)) {
            return;
        }
        messages = messages.filter(({ id }) => id !== data.message.id);
        filterEnabledMessages(messages);
    }
    
    function onMessageRemoved({ data }: RealTimeEvent): void {
        if (!messages.some(({ id }) => id === data.message.id)) {
            return;
        }
        messages = messages.filter(({ id }) => id !== data.message.id);
        filterEnabledMessages(messages);
    }

    function animateMessage(element: HTMLElement): void {

        let position = 1;
        let lastCall: number;
        let positionIsPositive = true;

        const next = (timestamp: number) => {
            if (lastCall === undefined) {
                lastCall = timestamp;
            }
            
            const deltaTime = (timestamp - lastCall) / 1000;
            lastCall = timestamp;
            position -= deltaTime * messagesVelocity;

            //Desde el momento en el que la posición del mensaje actual
            //pase a ser negativa llamamos al siguiente mensaje
            if (positionIsPositive && position < 0) {
                positionIsPositive = false;
                nextMessage();
            }

            element.style.setProperty('transform', `translateX(${100 * position}%)`);

            if (position < -1) {
                //La animación ha terminado
                animationsMap.delete(element);
                element.remove();
                return;
            }

            animationsMap.set(element, window.requestAnimationFrame(next));
        };

        animationsMap.set(element, window.requestAnimationFrame(next));
    }

</script>

<div bind:this={marqueeContainer} class="messages-marquee"></div>

<style lang="scss">

    .messages-marquee {
        position: relative;
        width: 100%;
        height: 100%;

        :global(.message) {
            position: absolute;
            top: 0;
            left: 0;;
            min-width: 100%;
            height: 100%;
            box-sizing: border-box;
            padding: 0 1rem;
            display: flex;
            align-items: center;
            color: #000;
            font-size: 3vw;
            text-transform: uppercase;
            transform: translateX(100%);
        }
    }

</style>
